import {
  paginateQueryToURLParams,
  PaginateQueryWithStats,
} from 'src/api/pagination/dtos';
import { apiClient } from 'src/careflow/ph-core/api_client/api_client';
import { Identifier } from 'src/careflow/ph-core/fhir-client/resources';
import { PatientDto } from '../dto/patient.dto';
import { CreateReferralDto } from 'src/crs/referral/dto/create-referral.dto';
import { UpdateReferralDto } from 'src/crs/referral/dto/update-referral.dto';

export interface PatientResponse {
  indexedCreatedBy: string;
  indexedCreatedAt: string;
  indexedUpdatedBy: string;
  indexedUpdatedAt: string;
  indexedPatienId: string;
  indexedPatientUUID: string;
  indexedFhirRefUri: string;
  indexedGivenName: string;
  indexedFamilyName: string;
  indexedFullName: string;
  indexedGender: string;
  indexedMrn: string;
  indexedPhone: string;
  indexedEmail: string;
  indexedDob: string;
  indexedRace: string;
  indexedEthnicity: string;
  indexedDOBFormatted: string;
  indexedAge: string;
  indexedCreatedAtFormatted: string;
  indexedCustomLastSil: string;
  indexedCustomIdentifierMedicaidId: string;
  indexedMetaLastUpdatedOnUtc: string;
  indexedStatus: string;
  indexedCustomEpisodeOfCare: object | null;
  indexedIdentifierJson: Identifier[] | null;
  indexedManagingOrgDisplay: string;
}

export interface PaginatedPatientResponse {
  data: PatientResponse[];
  links: {
    current: string;
  };
  meta: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface PaginatedPatientDto {
  data: PatientDto[];
  links: {
    current: string;
  };
  meta: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
  stats?: any;
}

const apiBase = 'crs/patient';
const PatientApi = {

  getAll: async (queryParams: PaginateQueryWithStats): Promise<PaginatedPatientResponse | undefined> => {
    const urlParams = paginateQueryToURLParams(queryParams);
    const url = `${apiBase}/?${urlParams.toString()}`;
    return apiClient.get<PaginatedPatientResponse | undefined>(url);
  },

  getOne: async (fhirId: string): Promise<PatientResponse | undefined> => {
    return apiClient.get<PatientResponse | undefined>(
      `${apiBase}/${fhirId}`
    );
  },

  create: async (payload: CreateReferralDto): Promise<PatientResponse | undefined> => {
    return apiClient.post<CreateReferralDto,PatientResponse | undefined>(
      `${apiBase}/`, payload
    );
  },

  update: async (fhirId: string, payload: UpdateReferralDto): Promise<PatientResponse | undefined> => {
    return apiClient.put<UpdateReferralDto,PatientResponse | undefined>(
      `${apiBase}/${fhirId}`, payload
    );
  },

  patch: async (fhirId: string, payload: UpdateReferralDto): Promise<PatientResponse | undefined> => {
    return apiClient.patch<UpdateReferralDto,PatientResponse | undefined>(
      `${apiBase}/${fhirId}`, payload
    );
  },
}

export const PatientService = {

  getAll: async (queryParams: PaginateQueryWithStats): Promise<PaginatedPatientDto | undefined> => {
    const patients = await PatientApi.getAll(queryParams);
    if (!patients) return undefined;
    const dtos = patients.data.map((r) => {
      const dto = new PatientDto();
      dto.fromPatientResponse(r);
      return dto;
    });
    const mapped: PaginatedPatientDto = { ...patients, data: dtos };
    return mapped;
  },

  create: async (payload: CreateReferralDto): Promise<PatientDto | undefined> => {
    const patient = await PatientApi.create(payload);
    if (!patient) return undefined;
    const dto = new PatientDto();
    dto.fromPatientResponse(patient);
    return dto;
  },

  update: async (fhirId: string, payload: UpdateReferralDto): Promise<PatientDto | undefined> => {
    const referral = await PatientApi.update(fhirId, payload);
    if (!referral) return undefined;
    const dto = new PatientDto();
    dto.fromPatientResponse(referral);
    return dto;
  }
}
